import { IDestroyOptions } from "pixi.js";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { TopPanel } from "by/gamefactory/cityversetycoon/scenes/game/components/toppanel/TopPanel";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { HourlyBonusResourcePackage } from "../resources/HourlyBonusResourcePackage";
import { IFortuneWheelModel } from "../../model/IFortuneWheelModel";
import { IFortuneWheelController } from "../controller/IFortuneWheelController";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { DailyWheelPage } from "./pages/DailyWheelPage";
import { GoldenWheelPage } from "./pages/GoldenWheelPage";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { PiggyWheelPage } from "./pages/PiggyWheelPage";
import { HourlyBonusState } from "../HourlyBonusState";
import { CancellationToken } from "../../../../../../shared/async/CancellationToken";

export class HourlyBonusPopupView extends BaseScalableView<void> {

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly playerModel: IPlayerModel;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly fortuneWheelController: IFortuneWheelController;
    private readonly logger: ILogger;
    private readonly state: HourlyBonusState;
    private readonly topPanel: TopPanel;

    public constructor(
        pcs: PromiseCompletionSource<void>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        wheelResources: HourlyBonusResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        playerModel: IPlayerModel,
        canvasScaler: ICanvasScaler,
        analyticsProvider: IAnalyticProviderWrapper,
        fortuneWheelModel: IFortuneWheelModel,
        fortuneWheelController: IFortuneWheelController,
        topPanel: TopPanel,
        state: HourlyBonusState,
        logger: ILogger
    ) {
        super(pcs, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.wheelResources = wheelResources;
        this.soundResources = soundResources;
        this.playerModel = playerModel;
        this.analyticsProvider = analyticsProvider;
        this.fortuneWheelModel = fortuneWheelModel;
        this.fortuneWheelController = fortuneWheelController;
        this.topPanel = topPanel;
        this.state = state;
        this.logger = logger;
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public async start(): Promise<void> {
        this.topPanel.enabled = false;
        this.topPanel.bringToFront();
        if (this.state === HourlyBonusState.PIGGY) {
            this.topPanel.show();
            await this.showPiggyWheelPagePage();
            this.operationControls.complete();
            return;
        }
        if (this.fortuneWheelModel.goldenWheelModel.value.isPaid.value) {
            await this.showGoldenWheelPage();
        } else {
            await this.showDailyWheelPage();
        }
        this.operationControls.complete();
    }

    private onCancellationRequested(): void {
        this.operationControls.complete();
        this.destroy();
    }

    private async showDailyWheelPage(): Promise<void> {
        const forceFreeWheel = this.state === HourlyBonusState.DAILY;
        const dailyPageCs = new PromiseCompletionSource<boolean>();
        const dailyPage = new DailyWheelPage(
            this.commonResources,
            this.wheelResources,
            this.soundResources,
            this.canvasScaler,
            this.analyticsProvider,
            this.playerModel,
            this.fortuneWheelModel,
            this.fortuneWheelController,
            dailyPageCs,
            forceFreeWheel,
            this.topPanel,
            this.logger
        );
        this.addChild(dailyPage);
        const isClose = await dailyPageCs.result;
        if (!isClose) {
            this.removeChild(dailyPage);
            await this.showGoldenWheelPage();
        }
    }

    private async showGoldenWheelPage(): Promise<void> {
        const goldenWheelPageCs = new PromiseCompletionSource<void>();
        const goldenWheelPage = new GoldenWheelPage(
            this.commonResources,
            this.cancellationToken,
            this.wheelResources,
            this.soundResources,
            this.canvasScaler,
            this.analyticsProvider,
            this.fortuneWheelModel,
            this.fortuneWheelController,
            this.playerModel,
            goldenWheelPageCs,
            this.logger
        );
        this.addChild(goldenWheelPage);
        await goldenWheelPageCs.result;
    }

    private async showPiggyWheelPagePage(): Promise<void> {
        const piggyWheelPcs = new PromiseCompletionSource<void>();
        const goldenWheelPage = new PiggyWheelPage(
            this.commonResources,
            this.wheelResources,
            this.soundResources,
            this.playerModel,
            this.canvasScaler,
            this.fortuneWheelModel,
            this.fortuneWheelController,
            piggyWheelPcs,
            this.topPanel,
            this.logger
        );
        this.addChild(goldenWheelPage);
        await piggyWheelPcs.result;
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.topPanel.bringToOriginParent();
        this.topPanel.enabled = true;
        super.destroy(options);
    }
}
